import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { CheckCircle2 } from 'lucide-react';
import { useApp } from '@/store';
import { useT } from '@/lib/i18n';

const CARD_W = 280;
const CARD_H = 110;
const MARGIN = 16;
const PAD = 8;
const AUTO_DISMISS_MS = 5000;

type Placement = { top: number; left: number; origin: string };

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function placeCard(rect: DOMRect | null): Placement {
  if (!rect) {
    return {
      top: window.innerHeight / 2 - CARD_H / 2,
      left: window.innerWidth / 2 - CARD_W / 2,
      origin: 'center',
    };
  }
  const rightSpace = window.innerWidth - (rect.right + PAD);
  const leftSpace = rect.left - PAD;
  const bottomSpace = window.innerHeight - (rect.bottom + PAD);
  const topSpace = rect.top - PAD;

  if (rightSpace >= CARD_W + MARGIN) {
    return {
      top: clamp(
        rect.top + rect.height / 2 - CARD_H / 2,
        MARGIN,
        window.innerHeight - CARD_H - MARGIN
      ),
      left: rect.right + PAD + 12,
      origin: 'left center',
    };
  }
  if (leftSpace >= CARD_W + MARGIN) {
    return {
      top: clamp(
        rect.top + rect.height / 2 - CARD_H / 2,
        MARGIN,
        window.innerHeight - CARD_H - MARGIN
      ),
      left: rect.left - PAD - CARD_W - 12,
      origin: 'right center',
    };
  }
  if (bottomSpace >= CARD_H + MARGIN) {
    return {
      top: rect.bottom + PAD + 12,
      left: clamp(
        rect.left + rect.width / 2 - CARD_W / 2,
        MARGIN,
        window.innerWidth - CARD_W - MARGIN
      ),
      origin: 'top center',
    };
  }
  if (topSpace >= CARD_H + MARGIN) {
    return {
      top: rect.top - PAD - CARD_H - 12,
      left: clamp(
        rect.left + rect.width / 2 - CARD_W / 2,
        MARGIN,
        window.innerWidth - CARD_W - MARGIN
      ),
      origin: 'bottom center',
    };
  }
  return {
    top: window.innerHeight / 2 - CARD_H / 2,
    left: window.innerWidth / 2 - CARD_W / 2,
    origin: 'center',
  };
}

function scrollIntoViewIfNeeded(el: HTMLElement) {
  const r = el.getBoundingClientRect();
  if (
    r.top < 0 ||
    r.bottom > window.innerHeight ||
    r.left < 0 ||
    r.right > window.innerWidth
  ) {
    el.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  }
}

export function FocusSpot() {
  const focusSelector = useApp(s => s.focusSelector);
  const focusLabel = useApp(s => s.focusLabel);
  const setFocus = useApp(s => s.setFocus);
  const t = useT();
  const loc = useLocation();

  const [rect, setRect] = useState<DOMRect | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const rafRef = useRef<number | null>(null);

  // Poll for element when focusSelector or route changes
  useLayoutEffect(() => {
    setRect(null);
    setPlacement(null);
    if (!focusSelector) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 50;

    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(focusSelector) as HTMLElement | null;
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          scrollIntoViewIfNeeded(el);
          rafRef.current = requestAnimationFrame(() => {
            const r2 = el.getBoundingClientRect();
            setRect(r2);
            setPlacement(placeCard(r2));
          });
          return;
        }
      }
      attempts++;
      if (attempts >= maxAttempts) {
        setFocus(null);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const startId = setTimeout(tick, 80);
    return () => {
      cancelled = true;
      clearTimeout(startId);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [focusSelector, loc.pathname]);

  // Auto-dismiss
  useEffect(() => {
    if (!focusSelector) return;
    const id = setTimeout(() => setFocus(null), AUTO_DISMISS_MS);
    return () => clearTimeout(id);
  }, [focusSelector, loc.pathname]);

  // Reposition on resize
  useEffect(() => {
    if (!focusSelector || !rect) return;
    const onResize = () => {
      const el = document.querySelector(focusSelector) as HTMLElement | null;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect(r);
      setPlacement(placeCard(r));
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [focusSelector, rect]);

  if (!focusSelector || !placement) return null;

  const spot = rect
    ? {
        top: Math.max(0, rect.top - PAD),
        left: Math.max(0, rect.left - PAD),
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
      }
    : null;

  const springConfig = { type: 'spring' as const, stiffness: 260, damping: 30 };

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="focus-root"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[1900]"
        onClick={() => setFocus(null)}
        role="button"
        tabIndex={-1}
        aria-label="dismiss"
      >
        {/* SVG overlay with emerald spotlight */}
        <svg
          className="absolute inset-0 h-full w-full pointer-events-none"
          aria-hidden="true"
        >
          <defs>
            <mask id="focus-mask">
              <rect width="100%" height="100%" fill="white" />
              {spot && (
                <motion.rect
                  initial={false}
                  animate={{
                    x: spot.left,
                    y: spot.top,
                    width: spot.width,
                    height: spot.height,
                  }}
                  transition={springConfig}
                  rx={10}
                  ry={10}
                  fill="black"
                />
              )}
            </mask>
            <linearGradient id="focus-stroke" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#10b981" />
              <stop offset="100%" stopColor="#059669" />
            </linearGradient>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(15,23,42,0.65)"
            mask="url(#focus-mask)"
          />
          {spot && (
            <motion.rect
              initial={false}
              animate={{
                x: spot.left,
                y: spot.top,
                width: spot.width,
                height: spot.height,
              }}
              transition={springConfig}
              rx={10}
              ry={10}
              fill="none"
              stroke="url(#focus-stroke)"
              strokeWidth={2.5}
            />
          )}
        </svg>

        {/* Tooltip card */}
        <AnimatePresence mode="wait">
          <motion.div
            key="focus-card"
            initial={{ opacity: 0, scale: 0.9, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 260, damping: 24 }}
            className="absolute overflow-hidden rounded-2xl border bg-card shadow-2xl"
            style={{
              top: placement.top,
              left: placement.left,
              width: Math.min(CARD_W, window.innerWidth - 32),
              transformOrigin: placement.origin,
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Emerald progress bar */}
            <div className="h-1 w-full bg-emerald-500" />

            <div className="px-4 py-3 space-y-2">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-500 text-white shadow-sm">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                    {t('updates.new')}
                  </div>
                  <div className="text-sm font-semibold leading-snug text-foreground">
                    {focusLabel}
                  </div>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground pl-11">
                {t('updates.focusHint')}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
